// Edits on a sheet's table, each returning a new one. Tables are kept
// rectangular: every row as wide as the widest, and at least one cell.
// References in formulas follow rows and columns as they go in and out.

import {
  type Cell,
  moveRefs,
  type Range,
  shiftRefs,
} from '@/app/sheet/sheet';

export type { Range };

export type Table = string[][];

type Pos = { row: number, col: number };

export const inRange = (range: Range, { row, col }: Pos) =>
  row >= range.top && row <= range.bottom
  && col >= range.left && col <= range.right;

// the range's cells, row by row
const positions = ({ top, left, bottom, right }: Range): Pos[][] =>
  Array.from(
    { length: bottom - top + 1 },
    (_, r) =>
      Array.from(
        { length: right - left + 1 },
        (_, c) => ({ row: top + r, col: left + c }),
      ),
  );

const isFormula = (cell: string) => cell.startsWith('=');

const shiftFormulas = (
  table: Table,
  axis: 'row' | 'col',
  at: number,
  by: 1 | -1,
): Table =>
  table.map(cells =>
    cells.map(cell => isFormula(cell) ? shiftRefs(cell, axis, at, by) : cell)
  );

const width = (table: Table) => Math.max(1, ...table.map(r => r.length));

const grow = (table: Table, rows: number, cols: number): Table =>
  Array.from(
    { length: Math.max(rows, table.length, 1) },
    (_, r) => Array.from({ length: cols }, (_, c) => table[r]?.[c] ?? ''),
  );

export const normalize = (table: Table): Table =>
  grow(table, table.length, width(table));

export const setCell = (
  table: Table,
  row: number,
  col: number,
  value: string,
): Table =>
  grow(table, row + 1, Math.max(width(table), col + 1)).map((cells, r) =>
    r === row ? cells.map((cell, c) => (c === col ? value : cell)) : cells
  );

export const insertRow = (table: Table, at: number): Table => {
  const rows = shiftFormulas(normalize(table), 'row', at, 1);
  return [
    ...rows.slice(0, at),
    Array(width(rows)).fill(''),
    ...rows.slice(at),
  ];
};

export const deleteRow = (table: Table, at: number): Table => {
  const rows = shiftFormulas(
    normalize(table).filter((_, r) => r !== at),
    'row',
    at,
    -1,
  );
  return rows.length ? rows : normalize([Array(width(table)).fill('')]);
};

export const insertColumn = (table: Table, at: number): Table =>
  shiftFormulas(normalize(table), 'col', at, 1).map(
    cells => [...cells.slice(0, at), '', ...cells.slice(at)],
  );

export const deleteColumn = (table: Table, at: number): Table =>
  width(table) === 1
    ? normalize(table).map(() => [''])
    : shiftFormulas(
      normalize(table).map(cells => cells.filter((_, c) => c !== at)),
      'col',
      at,
      -1,
    );

// Writes values into cells, growing the table only for those that change
// something - a blank past the edge, where a COUNTBY block can show, is
// left out. With nothing to change, the same table comes back.
const write = (table: Table, writes: (Pos & { value: string })[]): Table => {
  const changed = writes.filter(w => (table[w.row]?.[w.col] ?? '') !== w.value);
  if (!changed.length) return table;
  const values = new Map(changed.map(w => [`${w.row},${w.col}`, w.value]));
  return grow(
    table,
    Math.max(...changed.map(w => w.row + 1)),
    Math.max(width(table), ...changed.map(w => w.col + 1)),
  ).map((cells, r) => cells.map((cell, c) => values.get(`${r},${c}`) ?? cell));
};

export const clearRange = (table: Table, range: Range): Table =>
  write(table, positions(range).flat().map(p => ({ ...p, value: '' })));

// Where pasted cells came from: references into that block follow them to
// where they land. A cut moves them, so references anywhere in the table
// to the block follow too.
export type Source = { range: Range, cut: boolean };

// Tab separated rows, as spreadsheets put on the clipboard, written from the
// range's top left corner down and to the right, growing the table to fit.
// A single value fills the whole range instead.
export const paste = (
  table: Table,
  range: Range,
  text: string,
  source?: Source,
): Table => {
  const block = text.replace(/\r/g, '').replace(/\n$/, '').split('\n')
    .map(line => line.split('\t').map(value => value.trim()));
  // a value from the source's top left corner landing at `to`
  const follow = (value: string, to: Pos) =>
    source && isFormula(value)
      ? moveRefs(
        value,
        source.range,
        to.row - source.range.top,
        to.col - source.range.left,
      )
      : value;
  const corner = { row: range.top, col: range.left };
  const writes = block.length === 1 && block[0].length === 1
    ? positions(range).flat().map(p => ({
      ...p,
      value: follow(block[0][0], p),
    }))
    : block.flatMap((cells, r) =>
      cells.map((value, c) => ({
        row: range.top + r,
        col: range.left + c,
        value: follow(value, corner),
      }))
    );
  const base = source?.cut
    ? table.map(cells => cells.map(cell => follow(cell, corner)))
    : table;
  return write(base, writes);
};

// A range as tab separated rows, to paste back: cells as written, formulas
// and all. A COUNTBY block's cells go as blanks when its formula comes
// along, which fills them again, and as the values they show when it doesn't.
export const copyRange = (cells: Cell[][], range: Range): string =>
  positions(range).map(row =>
    row.map(p => {
      const cell = cells[p.row]?.[p.col];
      if (!cell) return '';
      return cell.spilled && !inRange(range, cell.spilled)
        ? String(cell.value)
        : cell.raw;
    }).join('\t')
  ).join('\n');

// from the last down, so each index still points where it did
const descending = (from: number, to: number) =>
  Array.from({ length: Math.max(0, to - from + 1) }, (_, i) => to - i);

export const deleteRows = (table: Table, top: number, bottom: number) =>
  descending(top, Math.min(bottom, table.length - 1))
    .reduce((t, r) => deleteRow(t, r), table);

export const deleteColumns = (table: Table, left: number, right: number) =>
  descending(left, Math.min(right, width(table) - 1))
    .reduce((t, c) => deleteColumn(t, c), table);
